import { useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  formatRequestedModulesLabel,
  licenseRequestStatusLabel,
  LICENSE_REQUEST_MSG_COOLDOWN,
  LICENSE_REQUEST_MSG_REMINDER_SENT,
  LICENSE_REQUEST_MSG_REMINDER_TOO_SOON,
  requestChannelLabel,
  formatCooldownRemaining,
} from "@/lib/commercialLicenseRequests";
import { licenseTypeLabel } from "@/lib/licenseTypes";
import { remindLicenseRequest } from "@/lib/commercialLicenseRequestsApi";
import { toast } from "sonner";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Muestra bloqueo 72 h, datos de solicitud pendiente y botón «Reenviar aviso a BNT».
 */
export default function LicenseSubmissionGate({
  gate,
  onGateChange,
  testId = "license-submission-gate",
}) {
  const [reminding, setReminding] = useState(false);

  if (!gate?.blocked) return null;

  const onRemind = async () => {
    if (!gate?.pending_request_id || !gate?.can_remind) return;
    setReminding(true);
    try {
      const result = await remindLicenseRequest(gate.pending_request_id);
      onGateChange?.(result?.gate || gate);
      toast.success(LICENSE_REQUEST_MSG_REMINDER_SENT);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || LICENSE_REQUEST_MSG_REMINDER_TOO_SOON;
      toast.error(msg);
    } finally {
      setReminding(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-3 text-[11px] text-amber-950 space-y-2"
      data-testid={testId}
    >
      <p className="m-0 leading-snug">{gate.message || LICENSE_REQUEST_MSG_COOLDOWN}</p>
      <dl className="grid gap-1 m-0">
        <div>
          <dt className="inline font-medium">Enviada: </dt>
          <dd className="inline">{formatDateTime(gate.submitted_at)}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Medio: </dt>
          <dd className="inline">{requestChannelLabel(gate.request_channel)}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Estado: </dt>
          <dd className="inline">{licenseRequestStatusLabel(gate.status)}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Tipo de licencia: </dt>
          <dd className="inline">{licenseTypeLabel(gate.requested_license_type)}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Módulos: </dt>
          <dd className="inline">{formatRequestedModulesLabel(gate.requested_modules) || "—"}</dd>
        </div>
        {gate.cooldown_ends_at ? (
          <div>
            <dt className="inline font-medium">Nueva solicitud disponible en: </dt>
            <dd className="inline">{formatCooldownRemaining(gate.cooldown_ends_at)}</dd>
          </div>
        ) : null}
        {gate.reminder_count > 0 ? (
          <div>
            <dt className="inline font-medium">Avisos enviados: </dt>
            <dd className="inline">{gate.reminder_count}</dd>
          </div>
        ) : null}
      </dl>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs border-amber-400 text-amber-950"
          disabled={!gate.can_remind || reminding}
          onClick={onRemind}
          data-testid="license-request-remind-bnt"
        >
          {reminding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-1" />}
          Reenviar aviso a BNT
        </Button>
      </div>
      {!gate.can_remind && gate.remind_blocked_until ? (
        <p className="text-[10px] text-amber-800 m-0">{LICENSE_REQUEST_MSG_REMINDER_TOO_SOON}</p>
      ) : null}
    </div>
  );
}
